import { useState } from "react";
import style from "./pickers.module.css";
import classNames from "classnames";
import { getMondayBoardItems } from "frontend/api";
import * as Select from "frontend/ui-components/select/select";
import { useDebounce } from "usehooks-ts";
import { concat, includes, prop, propEq, propSatisfies, uniqBy } from "rambda";
import useSWR from "swr";

function Placeholder({
  text,
  disabled,
  theme = "light",
}: {
  text: string;
  disabled?: boolean;
  theme?: "light" | "dark";
}) {
  return <div className={classNames(style.placeholder, style[theme], disabled && style.disabled)}>{text}</div>;
}

export function ItemPicker({
  integrationId,
  boardId,
  selectedItemId,
  onItemIdSelected,
  theme = "light",
}: {
  integrationId: string;
  boardId?: string;
  selectedItemId?: string;
  onItemIdSelected: (id: string) => void;
  theme?: "light" | "dark";
}) {
  const [query, setQuery] = useState<string>("");
  const debouncedQuery = useDebounce(query);
  //todo: this needs just item names, not their full data, and it might make a big difference

  const defaultItems = useSWR(
    boardId ? `monday/board/${boardId}/items` : null,
    () => getMondayBoardItems(integrationId, boardId!, { groupId: undefined }).then(({ items }) => items),
    { revalidateOnFocus: false }
  );

  const byQuery = useSWR(
    boardId && debouncedQuery && debouncedQuery.length > 2 ? `monday/board/${boardId}/items?${debouncedQuery}` : null,
    () =>
      getMondayBoardItems(integrationId, boardId!, { groupId: undefined, query: debouncedQuery }).then(
        ({ items }) => items
      ),
    { revalidateOnFocus: false }
  );

  const includesQuery = includes(query.toLocaleLowerCase());
  const stringMatchesQuery = (s: string) => includesQuery(s.toLocaleLowerCase());

  const hasItems = !!defaultItems.data || byQuery.data;

  // Prepare list of items to show.
  // The naive approach is to do items = byQuery.data || defaultItems.data || []
  // I discovered this is not pleasant. suppose you have 25 (loaded) items and you start typing,
  // the results are filtered immediately and a request is sent to backend.
  // You already see 3 results matching your query on top, and when the request comes back the output
  // is abruptly replaced by 25 fresh results (which might or might not include the 3)
  // The better experience is to always take the initially loaded items, add the server's response, and
  // then filter.
  const items: any[] | undefined = hasItems
    ? uniqBy(prop("id"), concat(defaultItems.data || [], byQuery.data || []))
    : undefined;
  const filteredItems = items?.filter(propSatisfies(stringMatchesQuery, "name"));
  const selectedItem = selectedItemId ? items?.find(propEq(selectedItemId, "id")) : undefined;

  return (
    <Select.Select allowOpenOptions={!!hasItems} selectedId={selectedItemId || ""} setSelectedId={onItemIdSelected}>
      <Select.Trigger customClass={classNames(style.selectTrigger, style[theme])}>
        {!boardId ? (
          <Placeholder text="Select board first" theme={theme} />
        ) : defaultItems.isLoading ? (
          <Placeholder text="Loading..." disabled={true} theme={theme} />
        ) : !hasItems ? (
          <Placeholder text="No items were found in the board" disabled={true} theme={theme} />
        ) : (
          <>
            <div
              title={selectedItem}
              style={{ display: "inline-block", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}
            >
              {selectedItem?.name || "Select item"}
            </div>
            <Select.Arrow theme={theme} />
          </>
        )}
      </Select.Trigger>
      <Select.Popover className={classNames(style.background, style.popover, style.rounded, style[theme])}>
        <Select.Search searchValue={query} setSearchValue={setQuery} rounded={true} />
        <Select.OptionsList>
          <div className={style.popoverChildren}>
            {filteredItems &&
              filteredItems.length &&
              filteredItems.map((item: any) => (
                <Select.Option id={item.id} key={item.id}>
                  <div className={classNames(style.boardPickerOption, selectedItemId == item.id && style.selected)}>
                    {item.name}
                  </div>
                </Select.Option>
              ))}
            {(byQuery.isLoading || query != debouncedQuery) && (
              <Placeholder text="Searching in board..." disabled={true} theme={theme} />
            )}
            {!byQuery.isLoading &&
              query == debouncedQuery &&
              query.length > 2 &&
              filteredItems &&
              filteredItems!.length == 0 && (
                <Placeholder text="No items matching the search were found" disabled={true} theme={theme} />
              )}
          </div>
        </Select.OptionsList>
      </Select.Popover>
    </Select.Select>
  );
}
